// src/components/Aboutus.jsx
import { motion } from "framer-motion";
import { Users, Trophy, BookOpen } from "lucide-react";

const features = [
  { title: "Workshops", description: "Hands-on learning with latest technologies.", icon: BookOpen, color: "from-[#006699] to-[#0099cc]" },
  { title: "Networking", description: "Connect with experts and peers.", icon: Users, color: "from-[#006699] to-[#0099cc]" },
  { title: "Competitions", description: "Participate in challenges to grow skills.", icon: Trophy, color: "from-[#006699] to-[#0099cc]" },
];

export default function Aboutus() {
  return (
    <div id="about" className="relative w-full bg-[#f4f7f9] min-h-screen py-24 flex items-center overflow-hidden">
      {/* Giant Background Text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.55]">
        <h1 className="text-[12vw] sm:text-[14vw] font-black text-gray-300/80 whitespace-nowrap tracking-tighter leading-none">
          WHO ARE WE
        </h1>
      </div>

      {/* Texture */}
      <div className="absolute inset-0 opacity-[0.25] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col-reverse lg:flex-row gap-16 lg:gap-12 items-center">
        
        {/* Left - Content */}
        <motion.div 
          initial={{ opacity: 0, x: -40 }} 
          animate={{ opacity: 1, x: 0 }} 
          transition={{ duration: 0.8, delay: 0.2 }} 
          className="lg:w-7/12 space-y-10"
        >
          <div className="space-y-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h2 className="text-[#006699] font-bold tracking-widest uppercase text-sm mb-2 border-l-[3px] border-[#006699] pl-3">
                Who Are We?
              </h2>
              <h3 className="text-5xl sm:text-6xl font-black text-[#002233] leading-tight">
                Empowering <br />
                <span className="text-[#006699]">Innovators</span>
              </h3>
            </motion.div>
            
            <p className="text-lg text-gray-600 leading-relaxed font-medium max-w-xl">
              IEEE ISIMS SB is a student branch of IEEE at the Higher Institute of Computer
              Science and Multimedia of Sfax. It aims to promote innovation, technical
              skills, and professional growth through workshops, events, competitions,
              and collaboration opportunities with industry experts.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-4">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.6, delay: 0.5 + i * 0.1 }} 
                  className="flex items-start gap-4 group"
                >
                  <div className="p-3 bg-white shadow-md shadow-gray-200/50 rounded-none border-l-[3px] border-t-[3px] border-[#006699] group-hover:bg-[#006699] transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#006699] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#00334d]">{feature.title}</h4>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{feature.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Right - Logo in Creative Blue Shape */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ duration: 0.8, delay: 0.3 }} 
          className="lg:w-5/12 flex justify-center items-center w-full relative"
        >
          <div className="relative w-full max-w-[380px] sm:max-w-[450px] aspect-square flex items-center justify-center p-8">
            {/* Animated Organic Blue Shape */}
            <motion.div 
              animate={{ 
                borderRadius: [
                  "40% 60% 70% 30% / 40% 50% 60% 50%", 
                  "60% 40% 30% 70% / 50% 60% 40% 50%", 
                  "40% 60% 70% 30% / 40% 50% 60% 50%"
                ] 
              }}
              transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
              className="absolute inset-0 bg-gradient-to-br from-[#006699] to-[#00334d] shadow-[0_20px_50px_rgba(0,102,153,0.3)] overflow-hidden"
            >
              {/* Inner shape texture and light */}
              <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }} />
              <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
            </motion.div>

            {/* Logo Image */}
            <img 
              src="/logosb.png" 
              alt="IEEE ISIMS SB Logo" 
              className="relative z-10 w-[70%] h-[70%] object-contain drop-shadow-lg" 
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
